/**
 * KbSourcesPanel — inspect the RAW source material behind a knowledge-base scope BEFORE it is embedded:
 * the pasted text, fetched URLs, HTML scrapes, uploaded documents, and (when the admin distilled it first)
 * the LLM output that was actually chunked + embedded. Substance quality is auditable here — garbage in,
 * garbage out — so the KB gets used to its full potential. Read-only.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Search, FileText, Globe, Code, File, Sparkles } from 'lucide-react';
import { adminKbSources, adminKbSourceContent, adminKbTargets, type KbScope, type KbRawSourceRow, type KbRawSourceDoc, type KbSourceKind, type KbTargetAgent } from '../../services/adminService';

const card = 'rounded-2xl border border-secondary-200 dark:border-secondary-800 p-5';
const input = 'rounded-lg border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-3 py-2 text-sm focus:outline-none focus:border-secondary-500';

const kindIcon = (k: KbSourceKind) => {
  const c = 'w-4 h-4';
  if (k === 'url') return <Globe className={c} />;
  if (k === 'html') return <Code className={c} />;
  if (k === 'document') return <File className={c} />;
  if (k === 'llm') return <Sparkles className={c} />;
  return <FileText className={c} />;
};
const kb = (n: number) => `${(n / 1024).toFixed(1)} KB`;

const SourceDetail: React.FC<{ scope: KbScope; id?: string; source: string }> = ({ scope, id, source }) => {
  const [d, setD] = useState<KbRawSourceDoc | null>(null);
  const [err, setErr] = useState('');
  React.useEffect(() => {
    let alive = true;
    adminKbSourceContent(scope, id, source).then((r) => { if (alive) setD(r); }).catch((e) => { if (alive) setErr(e?.message || 'Failed'); });
    return () => { alive = false; };
  }, [scope, id, source]);
  if (err) return <div className="p-4 text-xs text-red-600">{err}</div>;
  if (!d) return <div className="p-4 flex items-center gap-2 text-xs text-secondary-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  return (
    <div className="p-4 space-y-3 bg-secondary-50 dark:bg-secondary-900/40">
      {d.url && <div className="text-xs"><span className="text-secondary-400">URL: </span><a href={d.url} target="_blank" rel="noreferrer" className="underline break-all">{d.url}</a></div>}
      {d.distilled && (
        <div className="text-xs">
          <div className="text-secondary-400 mb-1">Embedded text — distilled by {d.distilled_by} from the {d.original_kind} below {d.truncated ? '(truncated)' : ''}</div>
          <div className="whitespace-pre-wrap break-words rounded bg-white dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-800 p-2 font-mono text-[11px] max-h-72 overflow-auto">{d.text}</div>
        </div>
      )}
      <div className="text-xs">
        <div className="text-secondary-400 mb-1">{d.distilled ? `Original ${d.original_kind} source` : 'Embedded source text'} {(d.distilled ? d.original_truncated : d.truncated) ? '(truncated)' : ''}</div>
        <div className="whitespace-pre-wrap break-words rounded bg-white dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-800 p-2 font-mono text-[11px] max-h-96 overflow-auto">{d.distilled ? (d.original_text || '(original not retained)') : d.text}</div>
      </div>
    </div>
  );
};

const KbSourcesPanel: React.FC = () => {
  const [scope, setScope] = useState<KbScope>('domain');
  const [id, setId] = useState('');
  const [rows, setRows] = useState<KbRawSourceRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  // Targets for the pickers — the admin can't remember every id, so agent/domain are autocomplete lists.
  const [agents, setAgents] = useState<KbTargetAgent[]>([]);
  const [domains, setDomains] = useState<string[]>([]);
  useEffect(() => { adminKbTargets().then((t) => { setAgents(t.agents || []); setDomains(t.domains || []); }).catch(() => { /* ignore */ }); }, []);

  const load = useCallback(() => {
    if (scope !== 'all' && !id.trim()) { setErr('Enter an agent id or domain id.'); return; }
    setLoading(true); setErr(''); setOpen(null);
    adminKbSources(scope, id.trim() || undefined)
      .then((s) => setRows(s))
      .catch((e) => { setErr(e?.message || 'Failed to load sources'); setRows(null); })
      .finally(() => setLoading(false));
  }, [scope, id]);

  return (
    <div className={card}>
      <div className="mb-3">
        <h2 className="font-semibold">Knowledge sources</h2>
        <p className="text-xs text-secondary-500">The raw material — files, URLs, HTML scrapes, LLM output — behind each scope, before it is embedded.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select className={input} value={scope} onChange={(e) => { setScope(e.target.value as KbScope); setRows(null); }}>
          <option value="domain">Domain</option>
          <option value="agent">Agent</option>
          <option value="all">Global (all agents)</option>
        </select>
        {scope === 'domain' && (
          <select className={`${input} flex-1 min-w-[12rem]`} value={id} onChange={(e) => setId(e.target.value)}>
            <option value="">Select a domain…</option>
            {domains.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        )}
        {scope === 'agent' && (
          <>
            <input list="kb-agent-list" className={`${input} flex-1 min-w-[12rem]`} placeholder="Search agent by name or id…"
              value={id} onChange={(e) => setId(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} />
            <datalist id="kb-agent-list">
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name} — {a.id}</option>)}
            </datalist>
          </>
        )}
        <button onClick={load} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-secondary-900 dark:bg-white text-white dark:text-secondary-900 text-sm font-semibold"><Search className="w-4 h-4" /> Inspect</button>
      </div>
      {err && <div className="text-sm text-red-600 py-2">{err}</div>}
      {loading ? <div className="flex items-center gap-2 text-sm text-secondary-500 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        : rows === null ? null
        : !rows.length ? <div className="text-sm text-secondary-500 py-6">No retained raw sources for this scope (added before retention was enabled, or none ingested).</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-secondary-400 text-left text-xs uppercase tracking-wide">
                <tr><th className="py-1">Kind</th><th>Source</th><th>Size</th><th>Chars</th><th>Distilled</th><th>Added</th></tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <React.Fragment key={s.source}>
                    <tr onClick={() => setOpen(open === s.source ? null : s.source)} className="border-t border-secondary-100 dark:border-secondary-800 cursor-pointer hover:bg-secondary-50 dark:hover:bg-secondary-800/40">
                      <td className="py-1.5"><span className="inline-flex items-center gap-1.5 text-xs text-secondary-500">{kindIcon(s.kind)} {s.kind}</span></td>
                      <td className="max-w-[20rem] truncate">{s.source}</td>
                      <td className="text-xs whitespace-nowrap">{kb(s.bytes)}</td>
                      <td className="text-xs">{s.chars.toLocaleString()}</td>
                      <td className="text-xs">{s.distilled ? `yes (${s.distilled_by})` : 'no'}</td>
                      <td className="text-xs whitespace-nowrap text-secondary-500">{s.added_at ? new Date(s.added_at).toLocaleDateString() : '—'}</td>
                    </tr>
                    {open === s.source && <tr><td colSpan={6} className="p-0"><SourceDetail scope={scope} id={id.trim() || undefined} source={s.source} /></td></tr>}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            <div className="text-xs text-secondary-400 mt-2">{rows.length} source(s)</div>
          </div>
        )}
    </div>
  );
};

export default KbSourcesPanel;
